import React, { useState } from "react";
import { X } from "lucide-react";
import { Button } from "./ui/button";
import TelegramIcon from "./icons/TelegramIcon";
import api from "../lib/api";
import { SOCIAL_CHANNELS, openChannel } from "../lib/social";
import { useAccess } from "../context/AccessContext";

/**
 * TelegramRecommendCard
 * ────────────────────────────────────────────────────────────────────────
 * Premium-looking "Join our Telegram" card rendered near the top of the
 * Dashboard. Dismissal is persistent per-user (server-backed), so the
 * card never re-appears on any device once the user has interacted with
 * it — either clicking Join (implicit accept) or the ✕ (explicit no).
 *
 * Design goals:
 *   • Zero extra API call on mount — dismissal state piggy-backs on
 *     `/me/access.social_prefs.telegram_dismissed` which the app already
 *     fetches once per session.
 *   • Optimistic UI — the card hides instantly on click; the /dismiss
 *     POST is fire-and-forget. Refresh reconciles from server truth.
 *   • Follows the exact Card visual language used elsewhere on the
 *     Dashboard (rounded-2xl, dark:bg-zinc-900 border).
 */
export default function TelegramRecommendCard() {
  const { access, refresh } = useAccess();
  // Local "already-dismissed-in-this-render" gate. If the user acts, we
  // hide the card immediately without waiting for the POST or refresh.
  const [dismissedNow, setDismissedNow] = useState(false);

  const serverDismissed = !!access?.social_prefs?.[SOCIAL_CHANNELS.telegram.prefKey];
  if (dismissedNow || serverDismissed) return null;

  const persistDismiss = () => {
    setDismissedNow(true);
    api
      .post(SOCIAL_CHANNELS.telegram.dismissEndpoint)
      .catch(() => {
        // Non-critical: worst case the card reappears on next refresh.
        // Keep the user's session tidy by not surfacing this error.
      })
      .finally(() => {
        // Re-hydrate access so the next /dashboard load reflects the state.
        refresh?.();
      });
  };

  const onJoin = () => {
    openChannel("telegram");
    persistDismiss();
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-sky-500/25 bg-gradient-to-br from-sky-500/10 via-white to-white p-5 shadow-sm transition-shadow hover:shadow-md dark:border-sky-500/25 dark:from-sky-500/10 dark:via-zinc-900 dark:to-zinc-900"
      data-testid="telegram-recommend-card"
    >
      <button
        type="button"
        aria-label="Dismiss Telegram recommendation"
        data-testid="telegram-recommend-close"
        onClick={persistDismiss}
        className="absolute right-3 top-3 rounded-full p-1.5 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-white"
      >
        <X size={16} />
      </button>

      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        {/* Brand tile — official Telegram sky gradient, matches the icon
            colour so free users still recognise the channel. */}
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-500/25">
          <TelegramIcon size={24} />
        </div>

        <div className="flex-1 pr-6">
          <h3 className="font-heading text-lg font-semibold" data-testid="telegram-recommend-title">
            📢 Join the Official Syllox Telegram Channel
          </h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Get free Notes, DPPs, PYQs, study resources, feature updates,
            YouTube videos, and important announcements directly on Telegram.
          </p>
        </div>

        <Button
          onClick={onJoin}
          data-testid="telegram-recommend-join"
          className="w-full shrink-0 bg-sky-500 text-white hover:bg-sky-600 sm:w-auto"
        >
          <TelegramIcon size={16} className="mr-1.5" />
          Join Telegram
        </Button>
      </div>
    </div>
  );
}
